import { useState, type KeyboardEvent, type MouseEvent } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { HoverHintWithGlyph } from "@/components/ui/title-help";
import { formatDateTime } from "@/pages/manufacturing/workOrdersPageModel";

import {
  transferLineCartonSealedAt,
  transferLineFirstPackedAt,
  transferLineFirstPickedAt,
  transferOrderHasShipmentEvents,
  type TransferBoardOrder,
  type TransferBoardOrderLine,
} from "./transfersWorkbenchTypes";

function stopTransferCardClick(event: MouseEvent): void {
  event.stopPropagation();
}

function timestampOrDash(value: string | null | undefined): string {
  if (value == null || value.trim() === "") {
    return "—";
  }
  return formatDateTime(value);
}

export function TransferLinePickPackHoverHint({
  order,
  line,
}: {
  order: TransferBoardOrder;
  line: TransferBoardOrderLine;
}): JSX.Element {
  return (
    <div className="space-y-0.5 border-b border-slate-200 pb-1.5 text-left text-xs leading-snug">
      <p>
        <span className="text-slate-500">Picked </span>
        <span className="tabular-nums text-slate-800">
          {timestampOrDash(transferLineFirstPickedAt(order, line.sku_id))}
        </span>
      </p>
      <p>
        <span className="text-slate-500">Packed </span>
        <span className="tabular-nums text-slate-800">
          {timestampOrDash(transferLineFirstPackedAt(order, line.id))}
        </span>
      </p>
      <p>
        <span className="text-slate-500">Carton sealed </span>
        <span className="tabular-nums text-slate-800">
          {timestampOrDash(transferLineCartonSealedAt(order, line.id))}
        </span>
      </p>
    </div>
  );
}

export function TransferShipmentEventsInline({
  order,
}: {
  order: TransferBoardOrder;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (!transferOrderHasShipmentEvents(order)) {
    return null;
  }

  const notificationUrl =
    order.shipped_notification_url?.trim()
    || (order.shipped_notification_freshdesk_ticket_id != null
      && order.shipped_notification_freshdesk_ticket_id > 0
      ? `https://smartgridsolutions.freshdesk.com/a/tickets/${String(order.shipped_notification_freshdesk_ticket_id)}`
      : null);

  return (
    <div className="space-y-2" onClick={stopTransferCardClick} onKeyDown={(event: KeyboardEvent) => event.stopPropagation()}>
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          aria-expanded={open}
          aria-label={open ? "Collapse shipment events" : "Expand shipment events"}
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500 hover:text-slate-800"
          onClick={() => setOpen((current) => !current)}
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden />}
          Shipment events
        </button>
        <HoverHintWithGlyph hoverHint="Transfer-level kitting, packing start, mark shipped, mark delivered, and the Freshdesk shipped email. Hover a SKU on the line table for that line's pick, pack, and carton-sealed times." />
        {!open ? (
          <span className="text-xs text-slate-600">
            {order.shipped_at != null ? `Shipped ${formatDateTime(order.shipped_at)}` : null}
            {order.shipped_at != null && order.delivered_at != null ? " · " : null}
            {order.delivered_at != null ? `Delivered ${formatDateTime(order.delivered_at)}` : null}
          </span>
        ) : null}
      </div>
      {open ? (
        <dl className="grid gap-1 text-xs text-slate-700 sm:grid-cols-[9.5rem_minmax(0,1fr)]">
          <dt className="font-medium text-slate-500">Kitting pick started</dt>
          <dd className="tabular-nums text-slate-900">{timestampOrDash(order.first_picked_at)}</dd>
          <dt className="font-medium text-slate-500">Kitting pick last scan</dt>
          <dd className="tabular-nums text-slate-900">{timestampOrDash(order.last_picked_at)}</dd>
          <dt className="font-medium text-slate-500">Packing started</dt>
          <dd className="tabular-nums text-slate-900">{timestampOrDash(order.packing_started_at)}</dd>
          <dt className="font-medium text-slate-500">Marked shipped</dt>
          <dd className="tabular-nums text-slate-900">{timestampOrDash(order.shipped_at)}</dd>
          <dt className="font-medium text-slate-500">Marked delivered</dt>
          <dd className="tabular-nums text-slate-900">{timestampOrDash(order.delivered_at)}</dd>
          <dt className="font-medium text-slate-500">Shipped email</dt>
          <dd>
            {notificationUrl != null ? (
              <a
                href={notificationUrl}
                target="_blank"
                rel="noreferrer"
                className="text-brand-700 underline decoration-brand-300 underline-offset-2 hover:text-brand-900"
              >
                Open Freshdesk ticket
                {order.shipped_notification_freshdesk_ticket_id != null
                  ? ` #${String(order.shipped_notification_freshdesk_ticket_id)}`
                  : ""}
              </a>
            ) : (
              <span className="text-slate-400">No notification recorded</span>
            )}
          </dd>
        </dl>
      ) : null}
    </div>
  );
}
